import { useCallback, useEffect, useRef, useState } from 'react'

// For whatever reason (I suspect related to the fact that we also import it in
// worker.js) doing import Comlink (without the "* as") doesn't work,
// notwithstanding that allowSyntheticDefaultImports is true in our TSConfig.
import * as Comlink from 'comlink'

import useAlerts from 'contexts/Alerts'
import { useId } from 'hooks/useId'
import { usePageVisibility } from 'hooks/usePageVisibility'

import { isDevEnvironment } from '../../config'

import { Query, getUrlKey, useParsedQuery } from './queries'
import { PublicStore, SubscriberCb, Timestamped, isStoreError } from './store'
import SharedWorker from './worker?sharedworker'

export * from './queries'

const worker = new SharedWorker()

const store = Comlink.wrap<PublicStore<unknown>>(worker.port)

/**
 * Hook for running a query against an API endpoint periodically.
 *
 * The typical use-case is running a query against an endpoint that returns a
 * JSON array of objects. The type parameter defaults are set up to make this
 * use-case convenient (i.e. you only need to supply the first parameter,
 * corresponding to the shape of the objects contained by the returned array).
 * If you are running against an endpoint that returns a singleton (e.g. using
 * PostgREST single-object headers), then setting the second type parameter to
 * `false` allows you to override the implicit listification (`T` -> `T[]`) on
 * the return type.
 *
 * Since rules of hooks don't allow you to conditionally call usePollApi, you
 * can pass `undefined` as the `query` to unsubscribe.
 */
export function usePollApi<
  T,
  ReturnsArrayOfT extends boolean = true,
  R = ReturnsArrayOfT extends true ? T[] : T,
>(
  query: Query | undefined,
  delay = 1000,
  subscriptionCheckThreshold = Math.max(5000, delay * 2),
  /**
   * The default value of false means that usePollApi might return data
   * pertaining to a previously-passed query (which may be preferable to
   * undefined in some cases, e.g. when the UI is happy to show stale data).
   *
   * If you strictly only want usePollApi to return data which pertains to the
   * currently-supplied query, set this parameter to true.
   */
  shouldReturnDataForCurrentQueryOnly = false,
): R | undefined {
  const id = useId()
  const url = useParsedQuery(query)
  const key = url === undefined ? undefined : getUrlKey(url)
  const keyRef = useRef(key)
  const urlStr = url?.toString()
  const showAlerts = useAlerts()
  const [lastVisibilityChange, setLastVisibilityChange] = useState<
    number | undefined
  >()
  const visible = usePageVisibility(isVisible => {
    if (isVisible) {
      setLastVisibilityChange(Date.now())
    }
  })
  const [isDestroyed, setIsDestroyed] = useState(false)
  const disabled =
    typeof query === 'undefined' ||
    !visible ||
    delay === Infinity ||
    isDestroyed

  const [data, setData] = useState<Timestamped<R> | undefined>()
  const [lastForcedResubscribe, setLastForcedResubscribe] = useState<
    number | undefined
  >()

  const onDataReceived: SubscriberCb<R> = useCallback(
    (key, err, data) => {
      if (err) {
        // Unsubscribe the query if the store has been destroyed
        if (isStoreError(err)) {
          switch (err.reason) {
            case 'subscriber_destroyed':
              if (err.subscriberId === id) {
                setIsDestroyed(true)
              }
              return
            case 'force_resubscribed':
              if (err.key === key) {
                setLastForcedResubscribe(err.ts)
              }
              return
            default:
              return
          }
        } else {
          // Since we only call this callback if we're subscribed, and we only
          // subscribe if we have a url, url can't actually be undefined here.
          showAlerts({
            message: `Request error for ${url?.pathname}: ${err.message}`,
            style: 'danger',
          })
        }
        return
      }
      // This guards against active subscriptions that have registered a
      // callback prior to a query change and that run to completion before
      // being unsubscribed. The callback will eventually be garbage collected,
      // but while it remains active we don't want it overwriting data for the
      // current query with that returned for an outdated query.
      if (keyRef.current === key) {
        setData(data)
        setLastForcedResubscribe(undefined)
      }
    },
    [id, showAlerts, url?.pathname],
  )

  /** Check that the subscription has received data from the callback recently */
  const checkSubscriptionIsUpdating = useCallback(async () => {
    if (!data?.ts) return
    // If a subscription has stalled, we should attempt to force-resubscribe
    if (data.ts < Date.now() - subscriptionCheckThreshold && key) {
      // If we're in the middle of attempting to resubscribe, give the HTTP
      // request time to return, otherwise we'll keep dropping the store key
      // until the request runs faster than the checkSubscriptionIsUpdating loop
      if (
        lastForcedResubscribe === undefined ||
        Date.now() - lastForcedResubscribe > 10000
      ) {
        const isRecentlyVisible =
          lastVisibilityChange && Date.now() - lastVisibilityChange < 10000

        if (isRecentlyVisible) {
          // Silent resubscription after tab backgrounding - this is expected behavior
          // Show an info message in development
          if (isDevEnvironment()) {
            showAlerts({
              message: `Resubscribing to ${url?.pathname} after tab became visible`,
              style: 'info',
              timeout: 2000,
            })
          }
        } else {
          // Genuine query stall - show warning to user
          showAlerts({
            message: `Query for ${url?.pathname} appears stalled, forcing re-subscription`,
            style: 'warning',
            timeout: 3000,
          })
        }
        await store.forceResubscribe(key)
      }
    }
  }, [
    data?.ts,
    key,
    lastForcedResubscribe,
    lastVisibilityChange,
    showAlerts,
    subscriptionCheckThreshold,
    url?.pathname,
  ])

  /**
   * This effect does the initial subscription to a query, and unsubscribes if
   * the query changes or the component unmounts
   */
  useEffect(() => {
    // Don't bother subscribing if disabled. The `undefined` conditions are
    // redundant (if they are true then `disabled` is already true) but writing
    // them both exposes this fact to the typechecker.
    if (disabled || key === undefined || urlStr === undefined) {
      return
    }
    const subscribe = () =>
      store.subscribe(id, key, urlStr, delay, Comlink.proxy(onDataReceived))
    // make initial subscription
    subscribe()
    // periodically re-subscribe. This functions as a ping, in order to ensure
    // that the query is not garbage collected, but if the query is
    // inadvertently unmounted (e.g. this query is blocked and doesn't ping in
    // time), this will allow the query to be re-subscribed gracefully.
    const ping = setInterval(subscribe, 2000)
    return () => {
      clearInterval(ping)
      store.unsubscribe(id, key)
    }
  }, [delay, id, key, onDataReceived, urlStr, disabled])

  /** This effect keeps track of how long since we last got an update */
  useEffect(() => {
    if (disabled) {
      return
    }
    const check = setInterval(checkSubscriptionIsUpdating, 2000)
    return () => clearInterval(check)
  }, [checkSubscriptionIsUpdating, delay, disabled])

  if (keyRef.current !== key) {
    keyRef.current = key
    if (shouldReturnDataForCurrentQueryOnly) setData(undefined)
  }

  return data?.data
}
